import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import GHLPreviewModal from '../../components/GHLPreviewModal';
import { FiUsers, FiFileText, FiSend, FiTrendingUp, FiFilter, FiDownload, FiChevronUp, FiChevronDown, FiCheckCircle, FiChevronsUp, FiChevronsDown, FiRefreshCw } from 'react-icons/fi';

function AdminDashboard({ token }) {
  const [stats, setStats] = useState(null);
  const [agentStats, setAgentStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const [syncingGHL, setSyncingGHL] = useState(false);
  const [ghlPreviewModal, setGhlPreviewModal] = useState(false);
  const [ghlPreviewData, setGhlPreviewData] = useState(null);
  const [ghlPreviewLoading, setGhlPreviewLoading] = useState(false);
  const [ghlSaveLoading, setGhlSaveLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name'); // 'name', 'today', 'yesterday', 'bypassed', 'daily', 'monthly', 'total', 'remaining', or 'status'
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' or 'desc'
  const [agentFilter, setAgentFilter] = useState('all'); // 'all', 'active', or 'inactive'
  const [selectedAgent, setSelectedAgent] = useState(null); // For single agent view
  const [selectedAgentDetail, setSelectedAgentDetail] = useState(null); // Detailed stats for selected agent

  useEffect(() => {
    fetchDashboardData();
  }, [agentFilter]);

  const fetchDashboardData = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      
      // Build query params based on filter
      let queryParams = '';
      if (agentFilter === 'active') {
        queryParams = '?enabledOnly=true';
      } else if (agentFilter === 'inactive') {
        queryParams = '?enabledOnly=false';
      }
      
      console.log(`[AdminDashboard] Fetching dashboard stats with query: ${queryParams}`);
      
      const response = await axios.get(`/api/admin/dashboard/stats${queryParams}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log(`[AdminDashboard] Received ${response.data.agents.length} agents from API`);
      
      // ⭐ CLIENT-SIDE SAFETY FILTER - Exclude pending agents from dashboard
      const activatedAgents = response.data.agents.filter(a => a.accountStatus !== 'pending');
      console.log(`[AdminDashboard] Filtered ${response.data.agents.length} -> ${activatedAgents.length} agents (excluded pending)`);
      
      setStats(response.data.summary);
      setAgentStats(activatedAgents);
      setLastRefreshed(new Date());
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    fetchDashboardData(true);
  };

  // Step 1 — Fetch counts from GHL and show the preview modal (no DB write yet)
  const handleOpenGHLPreview = async () => {
    setGhlPreviewLoading(true);
    setGhlPreviewData(null);
    setGhlPreviewModal(true);
    try {
      const response = await axios.get('/api/admin/preview-ghl-counts', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGhlPreviewData(response.data);
    } catch (error) {
      console.error('Error fetching GHL preview:', error);
      setGhlPreviewData({ error: error.response?.data?.error || 'Failed to fetch counts from GHL' });
    } finally {
      setGhlPreviewLoading(false);
    }
  };

  // Step 2 — Admin confirmed the preview looks correct, now actually save to DB
  const handleConfirmGHLSync = async () => {
    setGhlSaveLoading(true);
    try {
      const response = await axios.post('/api/admin/sync-ghl-counts', {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const { synced, failed, noCount } = response.data;
      setGhlPreviewModal(false);
      setGhlPreviewData(null);
      alert(`✅ GHL counts saved to DB!\nUpdated: ${synced}  |  Failed: ${failed}  |  No count: ${noCount}\n\nDashboard will now refresh.`);
      fetchDashboardData(true);
    } catch (error) {
      console.error('Error saving GHL counts:', error);
      alert('❌ Failed to save GHL counts to DB. Check console for details.');
    } finally {
      setGhlSaveLoading(false);
    }
  };

  // Legacy one-shot handler kept for backward compat (not used in UI anymore)
  const handleSyncGHLCounts = handleConfirmGHLSync;

  const exportToCSV = () => {
    const headers = ['Agent Name', 'Email', 'Today', 'Yesterday', 'Total Sent', 'Daily Cap', 'Monthly Cap', 'Total Purchased', 'Daily Remaining', 'Monthly Remaining', 'Total Remaining', 'Enabled'];
    const rows = filteredAndSortedAgents.map(agent => [
      agent.name,
      agent.email,
      agent.todayCount || 0,
      agent.yesterdayCount || 0,
      agent.totalSent || 0,
      agent.dailyCap || 0,
      agent.monthlyCap || 0,
      agent.totalPurchased || 0,
      agent.dailyRemaining || 0,
      agent.monthlyRemaining || 0,
      agent.totalRemaining || 0,
      agent.enabled ? 'Yes' : 'No'
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `agent-report-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      // Toggle direction if same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New column, default to descending
      setSortBy(column);
      setSortDirection('desc');
    }
  };

  const handleAgentSelect = (agent) => {
    setSelectedAgent(agent);
    setSelectedAgentDetail(agent);
  };

  const handleCloseDetail = () => {
    setSelectedAgent(null);
    setSelectedAgentDetail(null);
  };

  const filteredAndSortedAgents = agentStats
    .filter(agent => 
      agent.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      agent.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      agent.ghlLocationName?.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      let aVal, bVal;
      
      switch (sortBy) {
        case 'name':
          aVal = (a.agentType === 'group' ? a.ghlLocationName || a.name : a.name) || '';
          bVal = (b.agentType === 'group' ? b.ghlLocationName || b.name : b.name) || '';
          return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
        
        case 'today':
          aVal = a.todayCount || 0;
          bVal = b.todayCount || 0;
          return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
        
        case 'yesterday':
          aVal = a.yesterdayCount || 0;
          bVal = b.yesterdayCount || 0;
          return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
        
        case 'daily':
          aVal = a.dailySent || 0;
          bVal = b.dailySent || 0;
          return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
        
        case 'monthly':
          aVal = a.monthlySent || 0;
          bVal = b.monthlySent || 0;
          return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
        
        case 'total':
          aVal = a.totalSent || 0;
          bVal = b.totalSent || 0;
          return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
        
        case 'remaining':
          aVal = (a.totalPurchased || 0) - (a.totalSent || 0);
          bVal = (b.totalPurchased || 0) - (b.totalSent || 0);
          return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
        
        case 'status':
          aVal = a.enabled ? 1 : 0;
          bVal = b.enabled ? 1 : 0;
          return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
        
        default:
          return 0;
      }
    });

  if (loading) return <div className="loading">Loading dashboard...</div>;

  // Determine which agents to use for stats: if searching, use filtered agents; otherwise use all agents
  const agentsForStats = searchTerm ? filteredAndSortedAgents : agentStats;

  // Calculate total remaining leads from selected agents (all or filtered)
  const totalPurchasedLeads = agentsForStats.reduce((sum, agent) => sum + (agent.totalPurchased || 0), 0);
  const totalSentLeads = agentsForStats.reduce((sum, agent) => sum + (agent.totalSent || 0), 0);
  const totalRemainingLeads = totalPurchasedLeads - totalSentLeads;
  const remainingPercentage = totalPurchasedLeads > 0 ? ((totalRemainingLeads / totalPurchasedLeads) * 100).toFixed(1) : 0;
  
  // Calculate stats summary (updated when searching)
  const statsForDisplay = !searchTerm ? stats : {
    totalAgents: agentsForStats.length,
    enabledAgents: agentsForStats.filter(a => a.enabled).length,
    leadsToday: agentsForStats.reduce((sum, agent) => sum + (agent.todayCount || 0), 0),
    leadsYesterday: agentsForStats.reduce((sum, agent) => sum + (agent.yesterdayCount || 0), 0),
    totalLeads: totalSentLeads,
    activeStates: [],
    activeStatesCount: 0
  };

  return (
    <div className="container">
      {/* Spin keyframe injected once */}
      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .spin { animation: spin 0.8s linear infinite; }
      `}</style>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h2 style={{ margin: 0 }}>Admin Dashboard</h2>
          {lastRefreshed && (
            <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#9ca3af' }}>
              Last updated: {lastRefreshed.toLocaleTimeString()}
            </p>
          )}
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {/* ⭐ Refresh Button */}
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 16px',
              borderRadius: '6px',
              border: '1px solid #d1d5db',
              background: refreshing ? '#f3f4f6' : 'white',
              color: refreshing ? '#9ca3af' : '#374151',
              fontWeight: '500',
              fontSize: '14px',
              cursor: refreshing ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s',
              minWidth: '110px',
              justifyContent: 'center'
            }}
            title="Refresh dashboard data"
          >
            <FiRefreshCw size={15} className={refreshing ? 'spin' : ''} />
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
          <button onClick={exportToCSV} className="btn btn-secondary" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FiDownload /> Export CSV
          </button>
        </div>
      </div>
      
      {/* Summary Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#4f46e5' }}>
            <FiUsers size={32} />
          </div>
          <div className="stat-content">
            <h3>{statsForDisplay?.totalAgents || 0}</h3>
            <p>Total Agents</p>
            <small style={{ color: '#10b981' }}>{statsForDisplay?.enabledAgents || 0} enabled</small>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#10b981' }}>
            <FiSend size={32} />
          </div>
          <div className="stat-content">
            <h3>{statsForDisplay?.leadsToday || 0}</h3>
            <p>Leads Today</p>
            <small style={{ color: '#6b7280' }}>leads distributed</small>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#f59e0b' }}>
            <FiFileText size={32} />
          </div>
          <div className="stat-content">
            <h3>{statsForDisplay?.leadsYesterday || 0}</h3>
            <p>Leads Yesterday</p>
            <small style={{ color: '#6b7280' }}>Compared to today</small>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#06b6d4' }}>
            <FiTrendingUp size={32} />
          </div>
          <div className="stat-content">
            <h3>{statsForDisplay?.totalLeads || 0}</h3>
            <p>Total Opportunities</p>
            <small style={{ color: '#6b7280' }}>All time distributed</small>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon" style={{ 
            background: totalRemainingLeads < 0 
              ? '#dc2626' 
              : totalRemainingLeads < totalPurchasedLeads * 0.2 
                ? '#f59e0b' 
                : '#10b981' 
          }}>
            <FiFileText size={32} />
          </div>
          <div className="stat-content">
            <h3 style={{ 
              color: totalRemainingLeads < 0 
                ? '#dc2626' 
                : totalRemainingLeads < totalPurchasedLeads * 0.2 
                  ? '#f59e0b' 
                  : '#10b981' 
            }}>
              {totalRemainingLeads.toLocaleString()}
            </h3>
            <p>Remaining Opportunities</p>
            <small style={{ 
              color: totalRemainingLeads < 0 
                ? '#dc2626' 
                : '#6b7280',
              fontWeight: '600'
            }}>
              {totalSentLeads.toLocaleString()} / {totalPurchasedLeads.toLocaleString()} sent ({remainingPercentage}% left)
            </small>
          </div>
        </div>
      </div>

      {/* Active States Card */}
      {stats?.activeStates && stats.activeStates.length > 0 && (
        <div className="section-card" style={{ marginTop: '32px' }}>
          <h3 style={{ marginBottom: '16px' }}>Active Coverage States ({stats.activeStatesCount})</h3>
          <p style={{ color: '#6b7280', marginBottom: '16px', fontSize: '14px' }}>
            States covered by enabled agents - use for Facebook ads geographic targeting
          </p>
          <div style={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: '8px',
            padding: '12px',
            background: '#f9fafb',
            borderRadius: '8px'
          }}>
            {stats.activeStates.map(state => (
              <span 
                key={state}
                className="badge"
                style={{
                  background: '#4f46e5',
                  color: 'white',
                  padding: '6px 12px',
                  fontSize: '13px',
                  fontWeight: '600',
                  borderRadius: '6px'
                }}
              >
                {state}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Agent Performance Table */}
      <div className="section-card" style={{ marginTop: '32px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
          <h3>Agent Performance</h3>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <div style={{ display: 'flex', gap: '4px', background: '#f3f4f6', padding: '4px', borderRadius: '6px' }}>
              <button
                onClick={() => setAgentFilter('all')}
                style={{
                  padding: '6px 14px',
                  borderRadius: '4px',
                  border: 'none',
                  background: agentFilter === 'all' ? '#4f46e5' : 'transparent',
                  color: agentFilter === 'all' ? 'white' : '#6b7280',
                  fontWeight: agentFilter === 'all' ? '600' : '500',
                  cursor: 'pointer',
                  fontSize: '13px',
                  transition: 'all 0.2s'
                }}
              >
                All Agents
              </button>
              <button
                onClick={() => setAgentFilter('active')}
                style={{
                  padding: '6px 14px',
                  borderRadius: '4px',
                  border: 'none',
                  background: agentFilter === 'active' ? '#10b981' : 'transparent',
                  color: agentFilter === 'active' ? 'white' : '#6b7280',
                  fontWeight: agentFilter === 'active' ? '600' : '500',
                  cursor: 'pointer',
                  fontSize: '13px',
                  transition: 'all 0.2s'
                }}
              >
                Active
              </button>
              <button
                onClick={() => setAgentFilter('inactive')}
                style={{
                  padding: '6px 14px',
                  borderRadius: '4px',
                  border: 'none',
                  background: agentFilter === 'inactive' ? '#ef4444' : 'transparent',
                  color: agentFilter === 'inactive' ? 'white' : '#6b7280',
                  fontWeight: agentFilter === 'inactive' ? '600' : '500',
                  cursor: 'pointer',
                  fontSize: '13px',
                  transition: 'all 0.2s'
                }}
              >
                Inactive
              </button>
            </div>
            <input
              type="text"
              placeholder="Search agents..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ 
                padding: '8px 12px', 
                borderRadius: '6px', 
                border: '1px solid #e5e7eb',
                minWidth: '200px'
              }}
            />
          </div>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table className="data-table">
            <thead>
              <tr>
                <th 
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => handleSort('name')}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    Agent
                    <div style={{ display: 'flex', flexDirection: 'column', marginLeft: 'auto' }}>
                      <FiChevronsUp size={12} style={{ opacity: sortBy === 'name' && sortDirection === 'asc' ? 1 : 0.3, marginBottom: '-4px' }} />
                      <FiChevronsDown size={12} style={{ opacity: sortBy === 'name' && sortDirection === 'desc' ? 1 : 0.3 }} />
                    </div>
                  </div>
                </th>
                <th 
                  style={{ textAlign: 'center', cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => handleSort('today')}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                    Today
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <FiChevronsUp size={12} style={{ opacity: sortBy === 'today' && sortDirection === 'asc' ? 1 : 0.3, marginBottom: '-4px' }} />
                      <FiChevronsDown size={12} style={{ opacity: sortBy === 'today' && sortDirection === 'desc' ? 1 : 0.3 }} />
                    </div>
                  </div>
                </th>
                <th 
                  style={{ textAlign: 'center', cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => handleSort('yesterday')}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                    Yesterday
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <FiChevronsUp size={12} style={{ opacity: sortBy === 'yesterday' && sortDirection === 'asc' ? 1 : 0.3, marginBottom: '-4px' }} />
                      <FiChevronsDown size={12} style={{ opacity: sortBy === 'yesterday' && sortDirection === 'desc' ? 1 : 0.3 }} />
                    </div>
                  </div>
                </th>
                {/* BYPASSED COLUMN - Commented out to save horizontal space
                <th 
                  style={{ textAlign: 'center', cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => handleSort('bypassed')}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                    Bypassed
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <FiChevronsUp size={12} style={{ opacity: sortBy === 'bypassed' && sortDirection === 'asc' ? 1 : 0.3, marginBottom: '-4px' }} />
                      <FiChevronsDown size={12} style={{ opacity: sortBy === 'bypassed' && sortDirection === 'desc' ? 1 : 0.3 }} />
                    </div>
                  </div>
                </th>
                */}
                <th 
                  style={{ textAlign: 'center', cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => handleSort('daily')}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                    Daily
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <FiChevronsUp size={12} style={{ opacity: sortBy === 'daily' && sortDirection === 'asc' ? 1 : 0.3, marginBottom: '-4px' }} />
                      <FiChevronsDown size={12} style={{ opacity: sortBy === 'daily' && sortDirection === 'desc' ? 1 : 0.3 }} />
                    </div>
                  </div>
                </th>
                <th 
                  style={{ textAlign: 'center', cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => handleSort('monthly')}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                    Monthly
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <FiChevronsUp size={12} style={{ opacity: sortBy === 'monthly' && sortDirection === 'asc' ? 1 : 0.3, marginBottom: '-4px' }} />
                      <FiChevronsDown size={12} style={{ opacity: sortBy === 'monthly' && sortDirection === 'desc' ? 1 : 0.3 }} />
                    </div>
                  </div>
                </th>
                <th 
                  style={{ textAlign: 'center', cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => handleSort('total')}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                    Total
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <FiChevronsUp size={12} style={{ opacity: sortBy === 'total' && sortDirection === 'asc' ? 1 : 0.3, marginBottom: '-4px' }} />
                      <FiChevronsDown size={12} style={{ opacity: sortBy === 'total' && sortDirection === 'desc' ? 1 : 0.3 }} />
                    </div>
                  </div>
                </th>
                <th 
                  style={{ textAlign: 'center', cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => handleSort('remaining')}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                    Remaining
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <FiChevronsUp size={12} style={{ opacity: sortBy === 'remaining' && sortDirection === 'asc' ? 1 : 0.3, marginBottom: '-4px' }} />
                      <FiChevronsDown size={12} style={{ opacity: sortBy === 'remaining' && sortDirection === 'desc' ? 1 : 0.3 }} />
                    </div>
                  </div>
                </th>
                <th 
                  style={{ textAlign: 'center', cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => handleSort('status')}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                    Status
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <FiChevronsUp size={12} style={{ opacity: sortBy === 'status' && sortDirection === 'asc' ? 1 : 0.3, marginBottom: '-4px' }} />
                      <FiChevronsDown size={12} style={{ opacity: sortBy === 'status' && sortDirection === 'desc' ? 1 : 0.3 }} />
                    </div>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredAndSortedAgents.length === 0 ? (
                <tr>
                  <td colSpan="9" style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>
                    No agents found
                  </td>
                </tr>
              ) : (
                filteredAndSortedAgents.map((agent) => {
                  const dailyPercent = agent.dailyCap > 0 ? (agent.dailySent / agent.dailyCap) * 100 : 0;
                  const monthlyPercent = agent.monthlyCap > 0 ? (agent.monthlySent / agent.monthlyCap) * 100 : 0;
                  const totalPercent = agent.totalPurchased > 0 ? (agent.totalSent / agent.totalPurchased) * 100 : 0;
                  const remaining = (agent.totalPurchased || 0) - (agent.totalSent || 0);

                  return (
                    <tr key={agent.email}>
                      <td>
                        <div>
                          <Link 
                            to={agent.agentType === 'group' && agent.ghlLocationId 
                              ? `/admin/agents/edit/group/${encodeURIComponent(agent.ghlLocationId)}`
                              : `/admin/agents/edit/${encodeURIComponent(agent.email)}`
                            } 
                            style={{ color: '#4f46e5', fontWeight: '500', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}
                          >
                            {agent.agentType === 'group' ? agent.ghlLocationName || agent.name : agent.name}
                            {agent.agentType === 'group' && (
                              <span style={{
                                background: 'linear-gradient(135deg, #9333EA 0%, #7C3AED 100%)',
                                color: 'white',
                                padding: '2px 6px',
                                borderRadius: '4px',
                                fontSize: '10px',
                                fontWeight: '700'
                              }}>
                                GROUP
                              </span>
                            )}
                          </Link>
                          <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '2px' }}>
                            {agent.agentType === 'group' ? `📍 ${agent.ghlLocationId}` : agent.email}
                          </div>
                        </div>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <span className="badge" style={{ background: '#dcfce7', color: '#166534' }}>
                          {agent.todayCount || 0}
                        </span>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <span className="badge" style={{ background: '#fef3c7', color: '#92400e' }}>
                          {agent.yesterdayCount || 0}
                        </span>
                      </td>
                      {/* BYPASSED DATA CELL - Commented out to save horizontal space
                      <td style={{ textAlign: 'center' }}>
                        <span 
                          className="badge" 
                          style={{ 
                            background: agent.bypassedLeadsCount > 0 ? '#fef3c7' : '#f3f4f6',
                            color: agent.bypassedLeadsCount > 0 ? '#92400e' : '#6b7280'
                          }}
                        >
                          {agent.bypassedLeadsCount || 0}
                        </span>
                      </td>
                      */}
                      <td style={{ textAlign: 'center' }}>
                        <span 
                          className="badge" 
                          style={{ 
                            background: dailyPercent >= 100 ? '#fee2e2' : dailyPercent >= 80 ? '#fef3c7' : '#dbeafe',
                            color: dailyPercent >= 100 ? '#991b1b' : dailyPercent >= 80 ? '#92400e' : '#1e40af'
                          }}
                        >
                          {agent.dailySent || 0}/{agent.dailyCap || 0}
                        </span>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <span 
                          className="badge" 
                          style={{ 
                            background: monthlyPercent >= 100 ? '#fee2e2' : monthlyPercent >= 80 ? '#fef3c7' : '#dbeafe',
                            color: monthlyPercent >= 100 ? '#991b1b' : monthlyPercent >= 80 ? '#92400e' : '#1e40af'
                          }}
                        >
                          {agent.monthlySent || 0}/{agent.monthlyCap || 0}
                        </span>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        {agent.totalSent === agent.totalPurchased && agent.totalPurchased > 0 ? (
                          // Fulfilled - show green badge with checkmark
                          <span 
                            className="badge" 
                            style={{ 
                              background: '#dcfce7',
                              color: '#166534',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              justifyContent: 'center'
                            }}
                          >
                            <FiCheckCircle size={14} />
                            Fulfilled {agent.totalSent}
                          </span>
                        ) : (
                          // Not fulfilled - show normal badge with color coding
                          <span 
                            className="badge" 
                            style={{ 
                              background: totalPercent >= 100 ? '#fee2e2' : totalPercent >= 80 ? '#fef3c7' : '#dbeafe',
                              color: totalPercent >= 100 ? '#991b1b' : totalPercent >= 80 ? '#92400e' : '#1e40af'
                            }}
                          >
                            {agent.hasGHLIntegration && '🌐 '}
                            {agent.totalSent || 0}/{agent.totalPurchased || 0}
                          </span>
                        )}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <span 
                          className="badge" 
                          style={{ 
                            background: remaining < 0 
                              ? '#fee2e2' 
                              : remaining < (agent.totalPurchased || 0) * 0.2 
                                ? '#fef3c7' 
                                : '#dcfce7',
                            color: remaining < 0 
                              ? '#991b1b' 
                              : remaining < (agent.totalPurchased || 0) * 0.2 
                                ? '#92400e' 
                                : '#166534',
                            fontWeight: '600'
                          }}
                        >
                          {remaining.toLocaleString()}
                        </span>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <span 
                          className="badge" 
                          style={{ 
                            background: agent.enabled ? '#dcfce7' : '#fee2e2',
                            color: agent.enabled ? '#166534' : '#991b1b'
                          }}
                        >
                          {agent.enabled ? 'Active' : 'Disabled'}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="section-card" style={{ marginTop: '32px' }}>
        <h3>Quick Actions</h3>
        <div className="action-buttons" style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
          <Link to="/admin/agents/new" className="btn btn-primary">
            + Add New Agent
          </Link>
          <Link to="/admin/requests" className="btn btn-secondary">
            View Change Requests
          </Link>
          <Link to="/admin/leads" className="btn btn-secondary">
            View Opportunities Distribution
          </Link>
          <button
            onClick={handleOpenGHLPreview}
            disabled={ghlPreviewLoading || ghlSaveLoading}
            className="btn btn-secondary"
            title="Preview live GHL contact counts before saving to the database."
            style={{ display: 'flex', alignItems: 'center', gap: '6px', opacity: (ghlPreviewLoading || ghlSaveLoading) ? 0.7 : 1 }}
          >
            {ghlPreviewLoading ? '⏳ Fetching from GHL…' : '🔄 Sync GHL Counts'}
          </button>
        </div>
        <p style={{ marginTop: '10px', fontSize: '12px', color: '#6b7280' }}>
          💡 <strong>Sync GHL Counts</strong> — Fetches live counts from GHL and shows a preview so you can verify before saving to DB. After the initial sync, counts auto-increment on every lead via <code>/track</code>.
        </p>
      </div>

      {ghlPreviewModal && (
      <GHLPreviewModal
        loading={ghlPreviewLoading}
        data={ghlPreviewData}
        saveLoading={ghlSaveLoading}
        onClose={() => { setGhlPreviewModal(false); setGhlPreviewData(null); }}
        onConfirm={handleConfirmGHLSync}
      />
    )}
    </div>
  );
}

export default AdminDashboard;
